import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListPeopleResult,
  type PersonReachResult,
} from '@whinerp/client';
import { Button, ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * The reporting line, as something an administrator can change and inspect
 * (F-005, G-011).
 *
 * Two halves were missing, and they were missing for the same reason.
 *
 * Three operations existed with no caller anywhere in the product -
 * setPersonRole, setRoleParent, setTeamMembership - so the org chart was drawn
 * and could not be edited. An operation with no caller is a promise the rest
 * of the system does not know it made.
 *
 * And escalation was a property of a WHERE clause. The hierarchy branch in
 * `visibilityPredicate` has always worked; nobody could look at it. F-005 asks
 * for the fix in one line: "Show a live 'this user can see N records' preview
 * when editing." The preview is the point of the editing - moving somebody in
 * an org chart without being able to see what it does to their access is how a
 * workspace discovers the answer from the person who should not have seen
 * something.
 *
 * The preview is fetched, never computed here. The server runs the same
 * predicate every list runs; a number worked out in the browser from the tree
 * would be a second answer to the question every list already answers, and the
 * administrator would believe this one.
 */

type People = ListPeopleResult;

export function OrgChartPanel({ people }: { people: People }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [inspecting, setInspecting] = useState<string | null>(null);

  // One invalidation for both mutations: moving a person and moving a role
  // both change the tree, the member counts and any open preview.
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['setup-people'] });
    void queryClient.invalidateQueries({ queryKey: ['person-reach'] });
  };

  const moveRole = useMutation<unknown, ApiError, { roleId: string; parentRoleId: string | null }>({
    mutationFn: (input) =>
      api.setRoleParent({ path: { roleId: input.roleId }, body: { parentRoleId: input.parentRoleId } }),
    onSuccess: refresh,
  });

  const movePerson = useMutation<unknown, ApiError, { userId: string; roleId: string | null }>({
    mutationFn: (input) =>
      api.setPersonRole({ path: { userId: input.userId }, body: { roleId: input.roleId } }),
    onSuccess: refresh,
  });

  const failure = moveRole.error ?? movePerson.error;

  return (
    <Panel data-region="org-chart" id="org-chart">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('people.roles')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('people.seesBelow')}</p>

        {/*
          * A refusal is shown where the control is.
          *
          * The database refuses a loop, and it is right to: the sharing
          * snapshot walks this tree on every authorised request, so one cycle
          * does not spoil a report, it hangs the application. What must not
          * happen is the select snapping back with no explanation.
          */}
        {failure ? (
          <p data-org-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        <ul className="flex flex-col">
          {people.roles.map((role) => (
            <li
              key={role.roleId}
              data-role={role.roleId}
              data-role-depth={role.depth}
              className="flex flex-wrap items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0"
            >
              {/* Indented at the start, so the tree reads correctly in a
                  right-to-left layout rather than always growing right. */}
              <span
                className="min-w-0 flex-1 truncate text-body text-ink"
                style={{ paddingInlineStart: `${String(role.depth)}rem` }}
              >
                {role.name}
              </span>
              <Num data-role-members={role.roleId} className="shrink-0 text-meta text-ink-meta">
                {role.members === 0
                  ? t('people.noMembers')
                  : t('people.members', { values: { count: formatCount(role.members, format) } })}
              </Num>
              <label className="flex shrink-0 items-center gap-1">
                <span className="sr-only">
                  {t('people.reportsTo', { values: { role: role.name } })}
                </span>
                {/*
                  * A select rather than drag.
                  *
                  * 04_Screens asks for drag to reparent. A select does the
                  * same job from a keyboard, from a screen reader and from a
                  * phone, and a tree that can only be changed by dragging is
                  * a tree some administrators cannot change at all.
                  */}
                <select
                  data-role-parent={role.roleId}
                  value={role.parentRoleId ?? ''}
                  disabled={moveRole.isPending}
                  onChange={(event) =>
                    moveRole.mutate({
                      roleId: role.roleId,
                      parentRoleId: event.target.value === '' ? null : event.target.value,
                    })
                  }
                  className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                >
                  <option value="">{t('people.reportsTo.none')}</option>
                  {people.roles
                    // Never itself. The database refuses the loop anyway; a
                    // control that offers a move it knows will be refused is a
                    // control that wastes somebody's afternoon to teach them.
                    .filter((candidate) => candidate.roleId !== role.roleId)
                    .map((candidate) => (
                      <option key={candidate.roleId} value={candidate.roleId}>
                        {candidate.name}
                      </option>
                    ))}
                </select>
              </label>
            </li>
          ))}
        </ul>

        <ul className="flex flex-col">
          {people.people.map((person) => (
            <li
              key={person.userId}
              data-org-person={person.userId}
              className="flex flex-wrap items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0"
            >
              <span className="min-w-0 flex-1 truncate text-body text-ink">{person.name}</span>
              {person.isWorkspaceAdmin ? (
                <span className="rounded-chip bg-sunken px-1.5 text-meta text-ink-600">
                  {t('people.admin')}
                </span>
              ) : null}
              <label className="flex shrink-0 items-center gap-1">
                <span className="sr-only">
                  {t('people.role.change', { values: { name: person.name } })}
                </span>
                <select
                  data-person-role={person.userId}
                  value={person.roleId ?? ''}
                  disabled={movePerson.isPending}
                  onChange={(event) =>
                    movePerson.mutate({
                      userId: person.userId,
                      roleId: event.target.value === '' ? null : event.target.value,
                    })
                  }
                  className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                >
                  <option value="">{t('people.role.none')}</option>
                  {people.roles.map((role) => (
                    <option key={role.roleId} value={role.roleId}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                intent="ghost"
                size="small"
                data-person-inspect={person.userId}
                onClick={() =>
                  setInspecting((current) => (current === person.userId ? null : person.userId))
                }
              >
                {t('people.inspect', { values: { name: person.name } })}
              </Button>
            </li>
          ))}
        </ul>

        {inspecting ? <Reach userId={inspecting} onClose={() => setInspecting(null)} /> : null}
      </div>
    </Panel>
  );
}

/**
 * What one person can see, and how much of it the reporting line gives them.
 *
 * Three figures per object, each labelled. Owned, visible, and visible only
 * because of the line all sound like one word - "records" - and a figure whose
 * scope is invisible is the defect that survives to production.
 */
function Reach({ userId, onClose }: { userId: string; onClose: () => void }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const reach = useQuery<PersonReachResult, ApiError>({
    queryKey: ['person-reach', userId],
    queryFn: () => api.personReach({ path: { userId } }),
  });

  if (reach.isPending) return <Skeleton className="h-block-md w-full" />;
  if (reach.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={reach.error.message}
        onRetry={() => void reach.refetch()}
      />
    );
  }

  const person = reach.data;
  return (
    <div
      data-reach={person.userId}
      data-reach-role={person.roleId ?? ''}
      className="flex flex-col gap-2 rounded-control bg-sunken p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-body text-ink">
          {t('people.reach.title', { values: { name: person.name } })}
        </span>
        <Button intent="ghost" size="small" data-reach-close onClick={onClose}>
          {t('people.reach.close')}
        </Button>
      </div>

      {/*
        * Why the numbers below are zero, when they are.
        *
        * "Nothing extra" and "everything already" are different answers, and
        * an administrator reading a column of zeroes with no sentence beside
        * it would reasonably conclude their own access was broken.
        */}
      {person.seesEverything ? (
        <p data-reach-everything className="text-meta text-ink-meta">
          {t('people.reach.everything', { values: { name: person.name } })}
        </p>
      ) : person.reports.length === 0 ? (
        <p data-reach-nobody className="text-meta text-ink-meta">
          {t('people.reach.nobody', { values: { name: person.name } })}
        </p>
      ) : (
        <p className="flex flex-wrap gap-1 text-meta text-ink-meta">
          <span>{t('people.reach.reports', { values: { name: person.name } })}:</span>
          {person.reports.map((report) => (
            <span
              key={report.userId}
              data-reach-report={report.userId}
              className="rounded-chip bg-surface px-1.5 text-ink-600"
            >
              {report.name}
            </span>
          ))}
        </p>
      )}

      <ul className="flex flex-col">
        {person.objects.map((entry) => (
          <li
            key={entry.objectKey}
            data-reach-object={entry.objectKey}
            data-reach-own={entry.own}
            data-reach-visible={entry.visible}
            data-reach-hierarchy={entry.throughHierarchy}
            className="flex flex-wrap items-baseline gap-x-3 border-t border-t-rule py-1 first:border-t-0"
          >
            <span className="min-w-0 flex-1 truncate text-dense text-ink">{entry.label}</span>
            {/* Each figure carries its own label. Three bare numbers in a row
                are three questions the reader has to guess at. */}
            <span className="text-meta text-ink-meta">
              {t('people.reach.own')}{' '}
              <Num className="text-ink">{formatCount(entry.own, format)}</Num>
            </span>
            <span className="text-meta text-ink-meta">
              {t('people.reach.visible')}{' '}
              <Num className="text-ink">{formatCount(entry.visible, format)}</Num>
            </span>
            <span className="text-meta text-ink-meta">
              {t('people.reach.hierarchy')}{' '}
              <Num className="text-ink">{formatCount(entry.throughHierarchy, format)}</Num>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
